import { MeetingContainerAPI, MeetingSourceData, MeetingSourceElement, MeetingSourceElementRef, MeetingSourceDeviceType, useMeetingSelector } from "@kalyzee/kast-web-sdk";
import { MeetingSourceExtended } from "../../hooks/meeting";
import React, { createRef, useCallback, useEffect, useMemo, useRef } from "react";
import { getAudioContextForVolumeAnalyzer } from "../../helpers/audio";
import { keepArrayIdentity } from "../../helpers/identity";
import { diagLogger } from "../../helpers/mediaDiagnostics";
import { MediaSourcesCarousel, MediaCarouselSource, CircleIcon, PrimaryButton } from "@kalyzee/kalyzee-ui";
import { toastError, toastWarning } from "../../helpers/toast";
import { useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { useAppStoreSelector } from "../../contexts/appStore/selectors";
import { useAppStoreDispatch } from "../../contexts/appStore/context";
import clsx from "clsx";

import styles from "./MediaSourcePicker.module.css";
import { MediaStreamExtended } from "@kalyzee/kast-web-sdk/webrtc";

export interface MediaSourcePickerProps {
  meetingAPI?: MeetingContainerAPI;
  /** When true (default when meetingAPI is set), sources without a backend media are greyed out with an error. Set to false for spectators configuring before having backend medias. */
  requireBackendMedia?: boolean;
  display?: boolean;
  sources: MeetingSourceExtended[];
  ignoreGestureError?: boolean;
  style?: React.CSSProperties;
  className?: string;
}

(window as any).tracks = {};

type MediaErrorKind = "gesture" | "notFound" | "permission" | "busy" | "constraints" | "unknown";

const MEDIA_ERROR_MESSAGES: Record<MediaErrorKind, { audio: string; video: string }> = {
  gesture: {
    audio: "Cliquez sur la page pour autoriser la lecture audio",
    video: "Cliquez sur la page pour autoriser la lecture vidéo",
  },
  notFound: {
    audio: "Aucun microphone détecté",
    video: "Aucune caméra détectée",
  },
  permission: {
    audio: "Veuillez autoriser le navigateur à accéder au microphone",
    video: "Veuillez autoriser le navigateur à accéder à la caméra",
  },
  busy: {
    audio: "Le microphone est déjà utilisé par une autre application",
    video: "La caméra est déjà utilisée par une autre application",
  },
  constraints: {
    audio: "Le microphone ne supporte pas la configuration demandée",
    video: "La caméra ne supporte pas la configuration demandée",
  },
  unknown: {
    audio: "Erreur d'accès au microphone",
    video: "Erreur d'accès à la caméra",
  },
};

/**
 * How long a side must stay without a device before it is reported missing. A
 * capture reports none while its child device (an OvenMedia stream, a
 * whiteboard, ...) has yet to register its virtual one.
 */
const DEVICE_LOSS_CONFIRMATION = 1500;

/**
 * Whether a source is supposed to capture that side from real hardware: it has
 * a device for it (the SDK already resolved mode, priority and disabled ones),
 * that device captures through the browser, and its filter doesn't rule out
 * every device. A side the source never captures must not be reported missing.
 */
const sourceCanCapture = (data: MeetingSourceData | undefined, kind: "audio" | "video") => {
  const device = data?.[kind].device;
  if (!device || device.type !== MeetingSourceDeviceType.WEB) return false;
  // An absent filter allows every device, an empty one forbids them all:
  // opposite meanings, so a falsy test would confuse them
  const filter = kind === "audio" ? device.data.filterAudioDevices : device.data.filterVideoDevices;
  return !(Array.isArray(filter) && filter.length === 0);
};

/**
 * The audio track the source really captures, if any. A source whose audio
 * device never resolved (no mic, none selected yet) still carries a track —
 * MediaCapture's silent fallback — which is mute by design: reporting it would
 * pin an audio level meter to zero forever. `label` is what tells the two
 * apart, the same read the missing-device flag is built on.
 */
const capturedAudioTrack = (data: MeetingSourceData): MediaStreamTrack | undefined => {
  if (!data.audio.device || data.audio.label === undefined) return undefined;
  return data.mediaExtended?.getAudioTracks(true)[0];
};

const classifyMediaError = (e: unknown): MediaErrorKind => {
  const err = e as { name?: string; message?: string } | undefined;
  const name = err?.name;
  const message = err?.message ?? String(e);
  if (message.includes("Gesture")) return "gesture";
  // MediaCapture emits the plain string 'No device' when the selection becomes
  // empty (device unplugged, deviceId gone from the list): not a permission issue
  if (message.includes("No device") || name === "NotFoundError" || name === "DevicesNotFoundError") return "notFound";
  if (name === "NotAllowedError" || name === "PermissionDeniedError" || name === "SecurityError") return "permission";
  if (name === "NotReadableError" || name === "TrackStartError" || name === "AbortError") return "busy";
  if (name === "OverconstrainedError" || name === "ConstraintNotSatisfiedError") return "constraints";
  return "unknown";
};

const _MediaSourcePicker: React.FC<MediaSourcePickerProps> = ({
  meetingAPI,
  requireBackendMedia = !!meetingAPI,
  display,
  sources,
  ignoreGestureError,
  style,
  className,
}) => {
  const meetingAPIRef = useRefAutoUpdate(meetingAPI);
  const sourcesRef = useRef<{ [key: string]: React.RefObject<MeetingSourceElementRef> }>({});

  const [session] = useMeetingSelector(meetingAPI, "session");
  const medias = session?.medias;
  const mediasRef = useRefAutoUpdate(medias);

  const dispatch = useAppStoreDispatch();
  const dispatchRef = useRefAutoUpdate(dispatch);

  // Per-source preferences from AppStore (persisted to localStorage)
  const sourcePrefs = useAppStoreSelector(state => state.sourcePreferences);
  const sourcePrefsRef = useRefAutoUpdate(sourcePrefs);

  // Source incremented with user device prefs.
  // Identity is preserved when the content is unchanged: MeetingSourceElement
  // rebuilds the media of a source whose object identity changed, which drops
  // the tracks being emitted (and the `default*Device` values below only apply
  // on the first mount anyway — afterwards MediaCapture ignores them).
  const lastSourcesWithPrefsRef = useRef<MeetingSourceExtended[]>();
  const sourcesWithPrefs = useMemo(() => {
    const next = sources.map(source => {
      const pref = source.name ? sourcePrefsRef.current[source.name] : undefined;
      const sourceWithPref = (pref?.audioDeviceId || pref?.videoDeviceId)
        ? {
            ...source,
            devices: source.devices.map(device => {
              if (device.type !== MeetingSourceDeviceType.WEB) return device;
              return {
                ...device,
                data: {
                  ...device.data,
                  ...(pref.audioDeviceId ? { defaultAudioDevice: { deviceId: pref.audioDeviceId } } : {}),
                  ...(pref.videoDeviceId ? { defaultVideoDevice: { deviceId: pref.videoDeviceId } } : {}),
                },
              };
            }),
          }
        : source;
      return sourceWithPref;
    });
    const stable = keepArrayIdentity(lastSourcesWithPrefsRef.current, next);
    lastSourcesWithPrefsRef.current = stable;
    return stable;
  }, [sources]);

  // Fallback to AppStore global values when no per-source preference exists
  const globalAudio = useAppStoreSelector(state => state.audioStreamEnabled);
  const globalVideo = useAppStoreSelector(state => state.videoStreamEnabled);

  const getSourcePref = useCallback((name: string) => ({
    audioEnabled: sourcePrefs[name]?.audioEnabled ?? globalAudio,
    videoEnabled: sourcePrefs[name]?.videoEnabled ?? globalVideo,
  }), [sourcePrefs, globalAudio, globalVideo]);

  const multipleVisible = useMemo(() => {
    return sources.filter(s => s.visible).length > 1;
  }, [sources]);

  // Resolve per-source enabled state: backend media if connected, config pref if not
  const getSourceState = useCallback((source: MeetingSourceExtended) => {
    const name = source.name!;
    const backendMedia = medias?.find(m => m.name === name);
    const isError = requireBackendMedia && !backendMedia;

    const audioEnabled = backendMedia ? backendMedia.audio.enabled : getSourcePref(name).audioEnabled;
    const videoEnabled = backendMedia ? backendMedia.video.enabled : getSourcePref(name).videoEnabled;

    return { backendMedia, isError, audioEnabled, videoEnabled };
  }, [medias, requireBackendMedia, getSourcePref]);

  // Apply enable/disable to MeetingSourceElement refs
  const lastApplyLogRef = useRef<{ [sourceName: string]: string }>({});
  useEffect(() => {
    sources.forEach((source) => {
      if (!source.name) return;
      const ref = sourcesRef.current[source.name]?.current;
      const state = getSourceState(source);
      // enableAudio/enableVideo return false when the underlying player ref is
      // not ready: the enable is lost until the next state change.
      const applied = ref && !state.isError
        ? { audio: ref.enableAudio(state.audioEnabled), video: ref.enableVideo(state.videoEnabled) }
        : undefined;
      const log = {
        refReady: !!ref,
        hasBackendMedia: !!state.backendMedia,
        isError: state.isError,
        audioEnabled: state.audioEnabled,
        videoEnabled: state.videoEnabled,
        applied,
      };
      const json = JSON.stringify(log);
      if (lastApplyLogRef.current[source.name] !== json) {
        lastApplyLogRef.current[source.name] = json;
        diagLogger.log(`picker apply "${source.name}"`, log);
      }
    });
  }, [sources, getSourceState]);

  // Sync general AppStore state + persist per-source preferences from resolved states
  useEffect(() => {
    let anyAudio = false;
    let anyVideo = false;
    sources.forEach((source) => {
      if (!source.name) return;
      const state = getSourceState(source);
      if (state.isError) return;
      if (state.audioEnabled) anyAudio = true;
      if (state.videoEnabled) anyVideo = true;
      dispatch("updateSourcePreference", { sourceName: source.name, audioEnabled: state.audioEnabled, videoEnabled: state.videoEnabled });
    });
    dispatch("setAudioStreamEnabled", anyAudio);
    dispatch("setVideoStreamEnabled", anyVideo);
  }, [sources, getSourceState, dispatch]);

  // Toggle handlers
  const toggleAudio = useCallback(async (source: MeetingSourceExtended) => {
    const name = source.name!;
    const state = getSourceState(source);
    diagLogger.log(`picker toggle audio "${name}"`, { enabled: state.audioEnabled, isError: state.isError, hasBackendMedia: !!state.backendMedia });
    if (state.isError) return;

    const newEnabled = !state.audioEnabled;

    if (state.backendMedia && meetingAPI && session) {
      // Connected: update via meeting API (per-media)
      await meetingAPI.updater.medias.update(session.id, state.backendMedia.id, {
        audio: { enabled: newEnabled },
      });
    } else {
      // Disconnected: update ref + save preference
      sourcesRef.current[name]?.current?.enableAudio(newEnabled);
    }
    dispatch("updateSourcePreference", { sourceName: name, audioEnabled: newEnabled });

    // Sync general AppStore state
    const anyAudio = sources.some(s => {
      if (!s.name) return false;
      if (s.name === name) return newEnabled;
      const st = getSourceState(s);
      return st.audioEnabled;
    });
    dispatch("setAudioStreamEnabled", anyAudio);
  }, [getSourceState, meetingAPI, session, sources, dispatch]);

  const toggleVideo = useCallback(async (source: MeetingSourceExtended) => {
    const name = source.name!;
    const state = getSourceState(source);
    diagLogger.log(`picker toggle video "${name}"`, { enabled: state.videoEnabled, isError: state.isError, hasBackendMedia: !!state.backendMedia });
    if (state.isError) return;

    const newEnabled = !state.videoEnabled;

    if (state.backendMedia && meetingAPI && session) {
      await meetingAPI.updater.medias.update(session.id, state.backendMedia.id, {
        video: { enabled: newEnabled },
      });
    } else {
      sourcesRef.current[name]?.current?.enableVideo(newEnabled);
    }
    dispatch("updateSourcePreference", { sourceName: name, videoEnabled: newEnabled });

    const anyVideo = sources.some(s => {
      if (!s.name) return false;
      if (s.name === name) return newEnabled;
      const st = getSourceState(s);
      return st.videoEnabled;
    });
    dispatch("setVideoStreamEnabled", anyVideo);
  }, [getSourceState, meetingAPI, session, sources, dispatch]);

  const deviceLossTimersRef = useRef<{ [key: string]: ReturnType<typeof setTimeout> }>({});
  useEffect(() => {
    const timers = deviceLossTimersRef.current;
    return () => Object.values(timers).forEach(clearTimeout);
  }, []);

  /**
   * Availability is reported right away, absence only once it lasts: a device
   * appears late (a child registering its virtual device) far more often than
   * it goes away, and a flash of "no camera" on a stream that is merely
   * starting is worse than a second of silence.
   */
  const setSourceDevice = useCallback((sourceName: string, kind: "audio" | "video", present: boolean) => {
    const key = `${sourceName}:${kind}`;
    const timers = deviceLossTimersRef.current;
    if (timers[key]) {
      clearTimeout(timers[key]);
      delete timers[key];
    }
    const payload = { sourceName, ...(kind === "audio" ? { audio: present } : { video: present }) };
    if (present) {
      dispatchRef.current("updateSourceDevices", payload);
      return;
    }
    timers[key] = setTimeout(() => {
      delete timers[key];
      dispatchRef.current("updateSourceDevices", payload);
    }, DEVICE_LOSS_CONFIRMATION);
  }, []);

  /**
   * Which sides of a source still have hardware, from the data the `update`
   * event carries. That event is the only coherent read: `MeetingSource`
   * refreshes the labels then triggers it with the data as an argument, while
   * `getData()` called from another callback can catch `label` undefined
   * between a re-render and the capture ref being reattached — which would
   * report a missing camera while the stream is running.
   *
   * Both sides are always written, including the ones the source doesn't
   * capture: skipping them would let a `false` posted while the device was
   * still resolving survive forever, since resolving it can be what makes the
   * side stop looking like a hardware capture.
   */
  const onSourceData = useCallback((sourceName: string, data: MeetingSourceData) => {
    (["audio", "video"] as const).forEach((kind) => {
      setSourceDevice(sourceName, kind, !sourceCanCapture(data, kind) || data[kind].label !== undefined);
    });
  }, [setSourceDevice]);

  /**
   * Post the captured audio track. Read on both `track` and `update`: the
   * label and the track land on separate events whose order is not guaranteed,
   * so each one alone can read the other as missing. Whichever comes second
   * settles it, and unlike the flags above nothing here debounces, so a
   * transient miss only blanks the meter until the next event.
   */
  const onSourceAudioTrack = useCallback((sourceName: string, data: MeetingSourceData) => {
    dispatchRef.current("updateSelfAudioTrack", { sourceName, track: capturedAudioTrack(data) });
  }, []);

  // The guard above sits at write time, so a flag already stored outlives a
  // change of source definition. Give back the sides the source stopped
  // capturing. It only ever gives availability back, never takes it away, so
  // a stale read here cannot raise a false alarm.
  useEffect(() => {
    sources.forEach((source) => {
      if (!source.name) return;
      const data = sourcesRef.current[source.name]?.current?.getData();
      (["audio", "video"] as const).forEach((kind) => {
        if (!sourceCanCapture(data, kind)) setSourceDevice(source.name!, kind, true);
      });
    });
  }, [sources, setSourceDevice]);

  // Stream handling: updateSelfStream always, setMediaStream if connected
  const handleStreamChange = useCallback((sourceName: string, stream: MediaStreamExtended) => {
    dispatchRef.current("updateSelfStream", { sourceName, stream });

    const api = meetingAPIRef.current;
    if (!api) return;
    const mediaSession = mediasRef.current?.find((m) => m.name === sourceName);
    if (!mediaSession) return;
    api.setMediaStream(mediaSession.id, stream);
  }, []);

  // When new medias appear (e.g. spectator gets highlighted), attach existing streams
  const attachedMediaIdsRef = useRef(new Set<string>());

  useEffect(() => {
    if (!meetingAPI) return;

    const handler = meetingAPI.emissionManager.addEventListener("create", (ovenSession) => {
      const mediaSession = ovenSession.data.mediaSession;
      if (!mediaSession) return;
      if (attachedMediaIdsRef.current.has(mediaSession.id)) return;

      const mediaName = mediaSession.name;
      if (!mediaName) return;

      const sourceRef = sourcesRef.current[mediaName]?.current;
      const stream = sourceRef?.getData()?.mediaExtended;
      if (!stream) return;

      attachedMediaIdsRef.current.add(mediaSession.id);
      // Deferred past the SDK's own start, as in useDynamicMedia: attached
      // synchronously, the stream gets published twice (see there).
      setTimeout(() => {
        meetingAPI.setMediaStream(mediaSession.id, stream).then(() => {
          dispatchRef.current("updateSelfStream", { sourceName: mediaName, stream });
        });
      }, 0);
    });

    return () => handler.flush();
  }, [meetingAPI]);

  const sourceItems = useMemo(() => {
    return sourcesWithPrefs.map<MediaCarouselSource>((source) => {
      if (!sourcesRef.current[source.name!]) sourcesRef.current[source.name!] = createRef();
      const state = getSourceState(source);

      const content = (
        <div className={clsx(styles.sourceContainer, state.isError && styles.sourceDisabled)}>
          <div className={styles.sourceName}>{source.name}</div>
          <div className={styles.sourceContent}>
            <MeetingSourceElement
              className="meeting-source-expert"
              style={{ height: "100%", width: "100%", border: "unset", backgroundColor: "unset" }}
              ref={sourcesRef.current[source.name!]}
              key={source.name}
              source={source}
              using={!state.isError}
              audioContext={getAudioContextForVolumeAnalyzer()}
              keepAlwaysAudioTrackAliveDefault
              callbacks={{
                track: (_track, data) => {
                  if (data.mediaExtended && source.name) handleStreamChange(source.name, data.mediaExtended);
                  if (source.name) onSourceAudioTrack(source.name, data);
                },
                update: (data) => {
                  if (data.mediaExtended && source.name) handleStreamChange(source.name, data.mediaExtended);
                  if (source.name) {
                    onSourceData(source.name, data);
                    onSourceAudioTrack(source.name, data);
                  }
                },
                deviceSelected: (deviceInfo) => {
                  diagLogger.log(
                    `source "${source.name}" deviceSelected`,
                    deviceInfo
                      ? { kind: deviceInfo.kind, label: deviceInfo.label || "(empty)", id: deviceInfo.deviceId ? `${deviceInfo.deviceId.slice(0, 8)}…` : "(empty)" }
                      : "none",
                  );
                  if (!deviceInfo || !source.name) return;
                  const { kind, deviceId } = deviceInfo;
                  dispatchRef.current("updateSourcePreference", {
                    sourceName: source.name,
                    ...(kind === "audioinput" ? { audioDeviceId: deviceId } : {}),
                    ...(kind === "videoinput" ? { videoDeviceId: deviceId } : {}),
                  });
                },
                error: (type, _, __, e) => {
                  const err = e as { name?: string; message?: string } | undefined;
                  const kind = classifyMediaError(e);
                  diagLogger.log(`source "${source.name}" error (${type})`, { kind, name: err?.name, message: err?.message ?? String(e) });
                  if (!source.name) return;
                  // The SDK raises the event for both sides whatever the source
                  // captures, so a missing device is only news on a side it was
                  // supposed to capture. The flag itself is left to `update`,
                  // which reads coherent data.
                  if (kind === "notFound" && !sourceCanCapture(sourcesRef.current[source.name]?.current?.getData(), type)) return;
                  if (ignoreGestureError && kind === "gesture") return;
                  // Never disable the stream here: the device may come back and
                  // MediaCapture replaces the track in place when it does
                  const message = MEDIA_ERROR_MESSAGES[kind][type];
                  if (kind === "notFound") toastWarning(message);
                  else toastError(message);
                },
                player: (type, ref, data) => {
                  const currPlayer = data[type].devicePlayer;
                  const currParentPlayer = data[type].parentPlayer;
                  if (ref !== currPlayer && ref !== currParentPlayer) return;

                  // Curr player or parent player was just updated,
                  // We know the videoPlayerRef/audioPlayerRef
                  // inside MeetingSourceElement will be set
                  if (!source.name) return;
                  const state = getSourceState(source);
                  if (state.isError) return;
                  const sourceRef = sourcesRef.current[source.name]?.current;
                  if (!sourceRef) return;

                  if (type === "video") sourceRef.enableVideo(state.videoEnabled);
                  else sourceRef.enableAudio(state.audioEnabled);
                },
              }}
            />
            {state.isError && (
              <div className={styles.sourceError}>Source non disponible</div>
            )}
            {multipleVisible && (
              <div className={styles.sourceControls}>
                <PrimaryButton
                  className={clsx(state.isError && styles.controlBtnDisabled)}
                  onClick={state.isError ? undefined : () => toggleAudio(source)}
                >
                  <CircleIcon.Variants.Mic
                    className={styles.mediaBtn}
                    disabled={!state.audioEnabled}
                    unavailable={state.isError}
                  />
                </PrimaryButton>
                <PrimaryButton
                  className={clsx(state.isError && styles.controlBtnDisabled)}
                  onClick={state.isError ? undefined : () => toggleVideo(source)}
                >
                  <CircleIcon.Variants.Camera
                    className={styles.mediaBtn}
                    disabled={!state.videoEnabled}
                    unavailable={state.isError}
                  />
                </PrimaryButton>
              </div>
            )}
          </div>
        </div>
      );

      return {
        // By name: unique per buildMeetingSources, unlike the configured id
        id: `media-source-${source.name}`,
        name: source.name,
        content,
        visible: source.visible,
      };
    });
  }, [sourcesWithPrefs, getSourceState, multipleVisible, toggleAudio, toggleVideo, handleStreamChange, onSourceData, onSourceAudioTrack, meetingAPI, ignoreGestureError]);

  return (
    <MediaSourcesCarousel
      sources={sourceItems}
      display={display}
      style={style}
      className={clsx(styles.carouselContainer, className)}
    />
  );
}

export const MediaSourcePicker = React.memo(_MediaSourcePicker);

// ---- Global audio/video toggle (for disconnected pages: lobby, list) ----

export interface MediaGlobalControlsProps {
  sources: MeetingSourceExtended[];
  className?: string;
  style?: React.CSSProperties;
}

const _MediaGlobalControls: React.FC<MediaGlobalControlsProps> = ({ sources, className, style }) => {
  const dispatch = useAppStoreDispatch();
  const audioEnabled = useAppStoreSelector(state => state.audioStreamEnabled);
  const videoEnabled = useAppStoreSelector(state => state.videoStreamEnabled);

  const toggleAudio = useCallback(() => {
    const newEnabled = !audioEnabled;
    sources.forEach(s => {
      if (s.name) dispatch("updateSourcePreference", { sourceName: s.name, audioEnabled: newEnabled });
    });
    dispatch("setAudioStreamEnabled", newEnabled);
  }, [audioEnabled, sources, dispatch]);

  const toggleVideo = useCallback(() => {
    const newEnabled = !videoEnabled;
    sources.forEach(s => {
      if (s.name) dispatch("updateSourcePreference", { sourceName: s.name, videoEnabled: newEnabled });
    });
    dispatch("setVideoStreamEnabled", newEnabled);
  }, [videoEnabled, sources, dispatch]);

  return (
    <div className={clsx(styles.globalControls, className)} style={style}>
      <PrimaryButton onClick={toggleAudio}>
        <CircleIcon.Variants.Mic disabled={!audioEnabled} />
      </PrimaryButton>
      <PrimaryButton onClick={toggleVideo}>
        <CircleIcon.Variants.Camera disabled={!videoEnabled} />
      </PrimaryButton>
    </div>
  );
};

export const MediaGlobalControls = React.memo(_MediaGlobalControls);
